import { create } from 'zustand';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { ProtoAnnotationType } from './types';

// Map proto enum to string literals for easier use in React
// export type AnnotationTypeString =
//   | 'text'
//   | 'rectangle'
//   | 'circle'
//   | 'ellipse'
//   | 'line'
//   | 'polyline'
//   | 'polygon'
//   | 'path'
//   | 'highlighter'
//   | 'arrow'
//   | 'freehand'
//   | 'hyperlink'
//   | null;

// Optimistic shape interface
export interface OptimisticShape {
  id: string;
  objectId: string;
  type: AnnotationType;
  geometry: any;
  style: any;
  transform?: any;
  isdimensional?: boolean; // Include dimensional flag for optimistic shapes
  isOptimistic: true;
  createdAt: number;
}

export type AnnotationStore = {
  isAnnotating: boolean;
  activeAnnotationType: ProtoAnnotationType;
  isCreatingShape: boolean;
  shapeStartPoint: { x: number; y: number } | null;
  optimisticShapes: OptimisticShape[];
  // UI selection/editing state for shapes
  selectedShapeId: string | null;
  // Multi-select state
  // Deprecated: was used for toolbar toggle; selection is now group-aware by default
  isMultiSelectMode: boolean;
  selectedShapeIds: string[];
  isDraggingShape: boolean;
  // Live editing state for visual feedback
  liveEditingShapeId: string | null;
  liveEditingGeometry: any;
  liveEditingTransform: any;
  // Coordinated drag state for multiple shapes
  coordinatedDragActive: boolean;
  coordinatedDragGeometry: Map<string, any>;
  coordinatedDragTransform: Map<string, any>;
  // Cursor tooltip visibility
  showCursorTooltip: boolean;
  // Annotations visibility modes: 'all' | 'mine' | 'none'
  annotationVisibilityMode: 'all' | 'mine' | 'none';
  // Annotation editor state
  isEditingAnnotation: boolean;
  editingAnnotationId: string | null;
  editingAnnotationData: any | null;
  // The single source of truth for the currently edited/selected annotation
  currentAnnotation: any | null;
  // Copy/paste state
  copiedAnnotations: any[];
  isPastePreviewActive: boolean;
  pastePreviewAnnotation: any | null;
  // Cut state - track original item that was cut
  cutOriginalAnnotation: any | null;
  // Hyperlink two-step flow
  isHyperlinkAwaitingDestination: boolean;
  pendingHyperlinkSource: {
    objectId: string;
    position: { x: number; y: number };
    tempId: string;
    destinationObjectId?: string; // Store destination when user navigates
  } | null;
  // Leader creation flow
  isAddingLeader: boolean;
  addingLeaderForId: string | null;
  // Dimensional mode - whether annotations should be dimensional or non-dimensional
  isDimensionalMode: boolean;
  annotationStartingPoint: { x: number; y: number } | null;
  setAnnotationStartingPoint: (point: { x: number; y: number } | null) => void;
  setIsAnnotating: (isAnnotating: boolean) => void;
  setActiveAnnotationType: (activeAnnotationType: ProtoAnnotationType) => void;
  setIsCreatingShape: (isCreating: boolean) => void;
  setShapeStartPoint: (point: { x: number; y: number } | null) => void;
  addOptimisticShape: (shape: Omit<OptimisticShape, 'isOptimistic' | 'createdAt'>) => void;
  removeOptimisticShape: (id: string) => void;
  updateOptimisticShape: (id: string, updates: Partial<OptimisticShape>) => void;
  clearOptimisticShapes: () => void;
  setSelectedShapeId: (id: string | null) => void;
  // Multi-select actions
  // Deprecated: no-op retained for compatibility if referenced
  toggleMultiSelectMode: () => void;
  addSelectedShapeId: (id: string) => void;
  removeSelectedShapeId: (id: string) => void;
  clearSelectedShapeIds: () => void;
  setIsDraggingShape: (dragging: boolean) => void;
  setLiveEditingShape: (shapeId: string | null, geometry?: any, transform?: any) => void;
  setCoordinatedDragState: (
    active: boolean,
    geometry?: Map<string, any>,
    transform?: Map<string, any>
  ) => void;
  setShowCursorTooltip: (show: boolean) => void;
  setAnnotationVisibilityMode: (mode: 'all' | 'mine' | 'none') => void;
  // Annotation editor actions
  setIsEditingAnnotation: (isEditing: boolean) => void;
  setEditingAnnotation: (annotationId: string | null, annotationData?: any) => void;
  setCurrentAnnotation: (annotation: any | null) => void;
  // Copy/paste actions
  copyAnnotation: (annotation: any) => void;
  cutAnnotation: (annotation: any) => void;
  clearCopiedAnnotations: () => void;
  setCopiedAnnotations: (annotations: any[]) => void;
  // Cut management actions
  confirmCut: () => void;
  cancelCut: () => void;
  // Paste preview actions
  setPastePreview: (annotation: any | null, isActive: boolean) => void;
  updatePastePreviewPosition: (position: { x: number; y: number }) => void;
  // Helper to convert string type to proto enum
  getProtoAnnotationType: (type: ProtoAnnotationType) => AnnotationType;
  // Hyperlink flow actions
  setHyperlinkAwaitingDestination: (awaiting: boolean) => void;
  setPendingHyperlinkSource: (
    data: {
      objectId: string;
      position: { x: number; y: number };
      tempId: string;
      destinationObjectId?: string;
    } | null
  ) => void;
  // Leader creation actions
  startAddLeader: (annotationId: string) => void;
  stopAddLeader: () => void;
  // Dimensional mode actions
  setIsDimensionalMode: (isDimensional: boolean) => void;
};

export const useAnnotationStore = create<AnnotationStore>((set, get) => ({
  isAnnotating: false,
  activeAnnotationType: null,
  isCreatingShape: false,
  shapeStartPoint: null,
  optimisticShapes: [],
  selectedShapeId: null,
  isMultiSelectMode: false,
  selectedShapeIds: [],
  isDraggingShape: false,
  liveEditingShapeId: null,
  liveEditingGeometry: null,
  liveEditingTransform: null,
  coordinatedDragActive: false,
  coordinatedDragGeometry: new Map(),
  coordinatedDragTransform: new Map(),
  showCursorTooltip: true,
  annotationVisibilityMode: 'all',
  isEditingAnnotation: false,
  editingAnnotationId: null,
  editingAnnotationData: null,
  currentAnnotation: null,
  copiedAnnotations: [],
  isPastePreviewActive: false,
  pastePreviewAnnotation: null,
  cutOriginalAnnotation: null,
  isHyperlinkAwaitingDestination: false,
  pendingHyperlinkSource: null,
  isAddingLeader: false,
  addingLeaderForId: null,
  isDimensionalMode: false, // Default to non-dimensional mode
  annotationStartingPoint: null,
  setIsAnnotating: (isAnnotating: boolean) => set({ isAnnotating }),
  setActiveAnnotationType: (activeAnnotationType: ProtoAnnotationType) =>
    set({ activeAnnotationType }),
  setIsCreatingShape: (isCreating: boolean) => set({ isCreatingShape: isCreating }),
  setShapeStartPoint: (point: { x: number; y: number } | null) => set({ shapeStartPoint: point }),
  addOptimisticShape: shape =>
    set(state => ({
      optimisticShapes: [
        ...state.optimisticShapes,
        { ...shape, isOptimistic: true, createdAt: Date.now() },
      ],
      selectedShapeId: shape.id, // Automatically select the newly created shape
    })),
  removeOptimisticShape: id =>
    set(state => ({
      optimisticShapes: state.optimisticShapes.filter(s => s.id !== id),
    })),
  updateOptimisticShape: (id, updates) =>
    set(state => ({
      optimisticShapes: state.optimisticShapes.map(shape =>
        shape.id === id ? { ...shape, ...updates } : shape
      ),
    })),
  clearOptimisticShapes: () => set({ optimisticShapes: [] }),
  setSelectedShapeId: (id: string | null) =>
    set(state => {
      // If we're editing an annotation and a different shape is selected,
      // automatically switch the editor to the newly selected shape
      if (
        state.isEditingAnnotation &&
        state.editingAnnotationId &&
        id &&
        id !== state.editingAnnotationId
      ) {
        // Find the newly selected annotation data from the cache or store
        // For now, we'll just update the ID and let the component handle the data update
        return {
          selectedShapeId: id,
          editingAnnotationId: id,
          // Keep editingAnnotationData as is - it will be updated by the component
        };
      }
      return { selectedShapeId: id };
    }),
  // Multi-select actions
  toggleMultiSelectMode: () => set(state => ({ isMultiSelectMode: state.isMultiSelectMode })),
  addSelectedShapeId: (id: string) =>
    set(state => {
      if (state.selectedShapeIds.includes(id)) return state as any;
      return { selectedShapeIds: [...state.selectedShapeIds, id], selectedShapeId: id } as any;
    }),
  removeSelectedShapeId: (id: string) =>
    set(state => ({ selectedShapeIds: state.selectedShapeIds.filter(sid => sid !== id) })),
  clearSelectedShapeIds: () => set({ selectedShapeIds: [], selectedShapeId: null }),
  setIsDraggingShape: (dragging: boolean) => set({ isDraggingShape: dragging }),
  setLiveEditingShape: (shapeId: string | null, geometry?: any, transform?: any) =>
    set({
      liveEditingShapeId: shapeId,
      liveEditingGeometry: geometry,
      liveEditingTransform: transform,
    }),
  setCoordinatedDragState: (
    active: boolean,
    geometry?: Map<string, any>,
    transform?: Map<string, any>
  ) =>
    set({
      coordinatedDragActive: active,
      coordinatedDragGeometry: geometry || new Map(),
      coordinatedDragTransform: transform || new Map(),
    }),
  setShowCursorTooltip: (show: boolean) => set({ showCursorTooltip: show }),
  setAnnotationVisibilityMode: (mode: 'all' | 'mine' | 'none') =>
    set({ annotationVisibilityMode: mode }),

  // Annotation editor actions
  setIsEditingAnnotation: (isEditing: boolean) => set({ isEditingAnnotation: isEditing }),
  setEditingAnnotation: (annotationId: string | null, annotationData?: any) =>
    set({
      editingAnnotationId: annotationId,
      editingAnnotationData: annotationData || null,
      currentAnnotation: annotationData || null,
      isEditingAnnotation: !!annotationId,
    }),
  setCurrentAnnotation: (annotation: any | null) => set({ currentAnnotation: annotation }),

  // Copy/paste actions
  copyAnnotation: (annotation: any) =>
    set(state => ({
      copiedAnnotations: [...state.copiedAnnotations, annotation],
      isPastePreviewActive: true,
      pastePreviewAnnotation: { ...annotation, id: `preview-${Date.now()}` },
    })),
  cutAnnotation: (annotation: any) =>
    set(state => ({
      copiedAnnotations: [...state.copiedAnnotations, annotation],
      isPastePreviewActive: true,
      pastePreviewAnnotation: { ...annotation, id: `preview-${Date.now()}` },
      // Store the original annotation to restore if cut is cancelled
      cutOriginalAnnotation: annotation,
    })),
  clearCopiedAnnotations: () => set({ copiedAnnotations: [] }),
  setCopiedAnnotations: (annotations: any[]) => set({ copiedAnnotations: annotations }),

  // Cut management actions
  confirmCut: () =>
    set(state => ({
      // Clear cut state after successful paste
      cutOriginalAnnotation: null,
      isPastePreviewActive: false,
      pastePreviewAnnotation: null,
    })),
  cancelCut: () =>
    set(state => ({
      // Restore the original annotation and clear cut state
      cutOriginalAnnotation: null,
      isPastePreviewActive: false,
      pastePreviewAnnotation: null,
    })),

  // Paste preview actions
  setPastePreview: (annotation: any | null, isActive: boolean) =>
    set({
      pastePreviewAnnotation: annotation,
      isPastePreviewActive: isActive,
    }),
  updatePastePreviewPosition: (position: { x: number; y: number }) =>
    set(state => ({
      pastePreviewAnnotation: state.pastePreviewAnnotation
        ? {
            ...state.pastePreviewAnnotation,
            geometry: {
              ...state.pastePreviewAnnotation.geometry,
              x: position.x.toString(),
              y: position.y.toString(),
            },
          }
        : null,
    })),

  getProtoAnnotationType: (type: ProtoAnnotationType): AnnotationType => {
    switch (type) {
      case 'text':
        return AnnotationType.TEXT;
      case 'rectangle':
        return AnnotationType.RECTANGLE;
      case 'circle':
        return AnnotationType.CIRCLE;
      case 'ellipse':
        return AnnotationType.ELLIPSE;
      case 'line':
        return AnnotationType.LINE;
      case 'polyline':
        return AnnotationType.POLYLINE;
      case 'polygon':
        return AnnotationType.POLYGON;
      case 'path':
        return AnnotationType.PATH;
      case 'highlighter':
        return AnnotationType.HIGHLIGHTER;
      case 'arrow':
        return AnnotationType.ARROW;
      case 'freehand':
        return AnnotationType.FREEHAND;
      case 'hyperlink':
        return AnnotationType.HYPERLINK;
      default:
        return AnnotationType.TEXT;
    }
  },
  setHyperlinkAwaitingDestination: (awaiting: boolean) =>
    set({ isHyperlinkAwaitingDestination: awaiting }),
  setPendingHyperlinkSource: data => set({ pendingHyperlinkSource: data }),
  startAddLeader: (annotationId: string) =>
    set({ isAddingLeader: true, addingLeaderForId: annotationId, isEditingAnnotation: false }),
  stopAddLeader: () =>
    set({
      isAddingLeader: false,
      addingLeaderForId: null,
      liveEditingShapeId: null,
      liveEditingGeometry: null,
    }),
  setIsDimensionalMode: (isDimensional: boolean) => set({ isDimensionalMode: isDimensional }),
  setAnnotationStartingPoint: (point: { x: number; y: number } | null) =>
    set({ annotationStartingPoint: point }),
}));
